import { App as AntApp, Divider, Tabs, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUploadCalcSettingsAttachmentConfs } from "../../../../../../common/api/queries";
import ActionButton from "../../../../../../common/components/buttons/ActionButton";
import { SMALL_MAX_FILE_SIZE_MB } from "../../../../../../common/constants";
import { AttachmentConfType } from "../../../enums";
import { AttachmentConf, CalcSettings } from "../../../types";
import { AttachmentsConfList } from "./AttachmentsConfList";

interface Props {
  calcSettings: CalcSettings;
}

export type CustomAttachmentConf = AttachmentConf & { key: number };

const CalcSettingsAttachmentConfsPart = ({ calcSettings }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [activeTabKey, setActiveTabKey] = useState<AttachmentConfType>(AttachmentConfType.PROVIDED);

  const uploadCalcSettingsAttachmentConfs = useUploadCalcSettingsAttachmentConfs();

  const customAttachments = calcSettings.attachmentConfs.map<CustomAttachmentConf>((attachmentConf, index) => ({
    ...attachmentConf,
    key: index,
  }));

  const providedAttachments = customAttachments.filter(
    (attachmentConf) => attachmentConf.type === AttachmentConfType.PROVIDED,
  );

  const staticAttachments = customAttachments.filter(
    (attachmentConf) => attachmentConf.type === AttachmentConfType.STATIC,
  );

  const dynamicAttachments = customAttachments.filter(
    (attachmentConf) => attachmentConf.type === AttachmentConfType.DYNAMIC,
  );

  const handleAttachmentConfUpload = (file: RcFile, type: AttachmentConfType) => {
    const formData = new FormData();

    formData.append("types", type);
    formData.append("files", file);

    const isFileSizeAllowed = file.size / 1024 / 1024 < SMALL_MAX_FILE_SIZE_MB;

    if (!isFileSizeAllowed) {
      notification.error({
        title: t("common.error"),
        description: t("validation.unsupportedAttachmentSize", { size: SMALL_MAX_FILE_SIZE_MB }),
      });
    } else {
      uploadCalcSettingsAttachmentConfs.mutate({ object: formData, id: calcSettings.id });
    }

    return false;
  };

  return (
    <>
      <Divider titlePlacement="start">{t("calc.settings.sections.attachments")}</Divider>

      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => setActiveTabKey(key as AttachmentConfType)}
        tabBarExtraContent={
          activeTabKey !== AttachmentConfType.PROVIDED ? (
            <Upload
              showUploadList={false}
              multiple
              beforeUpload={(file) => handleAttachmentConfUpload(file, activeTabKey)}
            >
              <ActionButton icon="plus" variant="outlined">
                {t("calc.settings.actions.addAttachmentConf")}
              </ActionButton>
            </Upload>
          ) : undefined
        }
        items={[
          {
            key: AttachmentConfType.PROVIDED,
            forceRender: true,
            label: `${t("calc.settings.enums.attachmentConfType." + AttachmentConfType.PROVIDED)} (${providedAttachments?.length ?? 0})`,
            children: <AttachmentsConfList attachments={providedAttachments} calcSettings={calcSettings} />,
          },
          {
            key: AttachmentConfType.STATIC,
            forceRender: true,
            label: `${t("calc.settings.enums.attachmentConfType." + AttachmentConfType.STATIC)} (${staticAttachments?.length ?? 0})`,
            children: <AttachmentsConfList attachments={staticAttachments} calcSettings={calcSettings} />,
          },
          {
            key: AttachmentConfType.DYNAMIC,
            forceRender: true,
            label: `${t("calc.settings.enums.attachmentConfType." + AttachmentConfType.DYNAMIC)} (${dynamicAttachments?.length ?? 0})`,
            children: <AttachmentsConfList attachments={dynamicAttachments} calcSettings={calcSettings} />,
          },
        ]}
      />
    </>
  );
};

export default CalcSettingsAttachmentConfsPart;
